import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import { Key, Plus, Trash2, ToggleLeft, ToggleRight, Save, Copy, Eye, EyeOff, Info, Link, Code } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ApiKullanici {
    id: number;
    ad: string;
    apiKey: string;
    aktifMi: boolean;
    olusturmaTarihi: string;
}

interface ApiKullaniciCreated extends ApiKullanici {
    apiSecret: string;
}

interface LeadApiAyarlari {
    id: number;
    varsayilanKaynak: string | null;
    varsayilanDurum: number | null;
}

interface LeadDurum {
    statusID: number;
    statusAdi: string;
}

const KAYNAK_LISTESI = ['API', 'Web Sitesi', 'WhatsApp', 'Telefon', 'E-posta', 'Instagram', 'Facebook', 'Dijital'];

const card: React.CSSProperties = {
    background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: '24px',
};

const lbl: React.CSSProperties = {
    display: 'block', fontSize: 12, fontWeight: 500, color: '#6b7280', marginBottom: 6,
};

const inp: React.CSSProperties = {
    width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db',
    fontSize: 13, outline: 'none', background: '#fff', boxSizing: 'border-box',
};

const LeadApiAyarlariPage: React.FC = () => {
    const { t } = useTranslation();
    const [kullanicilar, setKullanicilar] = useState<ApiKullanici[]>([]);
    const [ayarlar, setAyarlar] = useState<LeadApiAyarlari>({ id: 0, varsayilanKaynak: 'API', varsayilanDurum: null });
    const [durumlar, setDurumlar] = useState<LeadDurum[]>([]);
    const [loading, setLoading] = useState(true);
    const [savingAyarlar, setSavingAyarlar] = useState(false);
    const [newAd, setNewAd] = useState('');
    const [creating, setCreating] = useState(false);
    const [createdSecret, setCreatedSecret] = useState<ApiKullaniciCreated | null>(null);
    const [showSecret, setShowSecret] = useState(false);
    const [activeTab, setActiveTab] = useState<'kullanicilar' | 'ayarlar' | 'kilavuz' | 'formkodu' | 'sheets'>('kullanicilar');

    // Form kodu üretici
    const [formApiKey, setFormApiKey] = useState('');
    const [formApiSecret, setFormApiSecret] = useState('');
    const [formBaslik, setFormBaslik] = useState(t('lead.formBaslik'));
    const [formButon, setFormButon] = useState(t('btn.gonder'));
    const [formBasari, setFormBasari] = useState('Teşekkürler! En kısa sürede sizinle iletişime geçeceğiz.');
    const [showFormSecret, setShowFormSecret] = useState(false);

    // Sheets entegrasyon state
    const KAYNAK_SHEETS = ['Google Sheets', 'Web Sitesi', 'WhatsApp', 'Instagram', 'Facebook', 'Dijital', 'Diğer'];
    interface SheetsEnt { id: number; ad: string; sheetsUrl: string; aralikDakika: number; aktifMi: boolean; sonSenkronTarihi: string | null; toplamAktarilan: number; sutunPk: number | null; sutunAdiSoyadi: number | null; sutunTelefon: number | null; sutunEposta: number | null; sutunAciklama: number | null; varsayilanKaynak: string | null; varsayilanDurumId: number | null; }
    const [sheetsListesi, setSheetsListesi] = useState<SheetsEnt[]>([]);
    const [sheetsLoading, setSheetsLoading] = useState(false);
    const [sheetsSyncing, setSheetsSyncing] = useState<number | null>(null);
    const [sheetsForm, setSheetsForm] = useState(false);
    const [sheetsEditId, setSheetsEditId] = useState<number | null>(null);
    const [sheetsUrl, setSheetsUrl] = useState('');
    const [sheetsAd, setSheetsAd] = useState('');
    const [sheetsAralik, setSheetsAralik] = useState(60);
    const [sheetsBasliklar, setSheetsBasliklar] = useState<string[]>([]);
    const [sheetsPreviewLoading, setSheetsPreviewLoading] = useState(false);
    const [sheetsSutunPk, setSheetsSutunPk] = useState<number | ''>('');
    const [sheetsSutunAdiSoyadi, setSheetsSutunAdiSoyadi] = useState<number | ''>('');
    const [sheetsSutunTelefon, setSheetsSutunTelefon] = useState<number | ''>('');
    const [sheetsSutunEposta, setSheetsSutunEposta] = useState<number | ''>('');
    const [sheetsSutunAciklama, setSheetsSutunAciklama] = useState<number | ''>('');
    const [sheetsVarsayilanKaynak, setSheetsVarsayilanKaynak] = useState('Google Sheets');
    const [sheetsVarsayilanDurum, setSheetsVarsayilanDurum] = useState<number | ''>('');
    const [sheetsSaving, setSheetsSaving] = useState(false);
    const [generatedCode, setGeneratedCode] = useState('');

    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            const [kulRes, ayarRes, durumRes] = await Promise.all([
                axiosInstance.get('/api/LeadApi/kullanicilar'),
                axiosInstance.get('/api/LeadApi/ayarlar'),
                axiosInstance.get('/api/LeadDurumlari'),
            ]);
            setKullanicilar(kulRes.data);
            setAyarlar(ayarRes.data);
            setDurumlar(durumRes.data);
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const handleCreateKullanici = async () => {
        if (!newAd.trim()) { toast.warning(t('msg.uyari')); return; }
        setCreating(true);
        try {
            const res = await axiosInstance.post('/api/LeadApi/kullanicilar', { ad: newAd.trim() });
            setCreatedSecret(res.data);
            setShowSecret(false);
            setNewAd('');
            setKullanicilar(prev => [res.data, ...prev]);
            toast.success(t('msg.islemBasarili'));
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setCreating(false);
        }
    };

    const handleToggle = async (id: number) => {
        try {
            const res = await axiosInstance.put(`/api/LeadApi/kullanicilar/${id}/toggle`);
            setKullanicilar(prev => prev.map(k => k.id === id ? { ...k, aktifMi: res.data.aktifMi } : k));
        } catch {
            toast.error(t('msg.islemHatasi'));
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm(t('confirm.silmekIstiyormusunuz'))) return;
        try {
            await axiosInstance.delete(`/api/LeadApi/kullanicilar/${id}`);
            setKullanicilar(prev => prev.filter(k => k.id !== id));
            toast.success(t('msg.islemBasarili'));
        } catch {
            toast.error(t('msg.islemHatasi'));
        }
    };

    const handleSaveAyarlar = async () => {
        setSavingAyarlar(true);
        try {
            const res = await axiosInstance.put('/api/LeadApi/ayarlar', {
                varsayilanKaynak: ayarlar.varsayilanKaynak,
                varsayilanDurum: ayarlar.varsayilanDurum,
            });
            setAyarlar(res.data);
            toast.success(t('msg.islemBasarili'));
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setSavingAyarlar(false);
        }
    };

    const copyToClipboard = (text: string, label: string) => {
        navigator.clipboard.writeText(text).then(() => toast.success(t('msg.islemBasarili')));
    };

    const apiBaseUrl = (axiosInstance.defaults.baseURL || 'http://localhost:5159').replace(/\/$/, '');

    const generateFormCode = () => {
        if (!formApiKey.trim() || !formApiSecret.trim()) {
            toast.warning('API Key ve API Secret giriniz');
            return;
        }
        const token = btoa(`${formApiKey.trim()}:${formApiSecret.trim()}`);
        const endpoint = `${apiBaseUrl}/api/LeadApi/submit`;
        const esc = (s: string) => s.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
        const code = `<!-- YapiCRM Potansiyel Müşteri Formu -->
<div id="yapicrm-form-wrap"></div>
<script>
(function(){
  var wrap = document.getElementById('yapicrm-form-wrap');
  wrap.innerHTML =
    '<form id="yapicrm-form" style="max-width:480px;font-family:Arial,sans-serif;padding:24px;border:1px solid #e5e7eb;border-radius:12px;background:#fff">'\
    + '<h3 style="margin:0 0 20px;font-size:18px;color:#111827">${esc(formBaslik)}</h3>'\
    + '<div style="margin-bottom:14px"><label style="display:block;font-size:13px;color:#374151;margin-bottom:6px">Ad Soyad *</label>'\
    +   '<input name="fullName" required placeholder="Ad\\u0131n\\u0131z Soyad\\u0131n\\u0131z" style="width:100%;padding:9px 12px;border:1px solid #d1d5db;border-radius:8px;font-size:14px;box-sizing:border-box"></div>'\
    + '<div style="margin-bottom:14px"><label style="display:block;font-size:13px;color:#374151;margin-bottom:6px">Telefon *</label>'\
    +   '<input name="phone" required placeholder="05xx xxx xx xx" style="width:100%;padding:9px 12px;border:1px solid #d1d5db;border-radius:8px;font-size:14px;box-sizing:border-box"></div>'\
    + '<div style="margin-bottom:14px"><label style="display:block;font-size:13px;color:#374151;margin-bottom:6px">E-Posta</label>'\
    +   '<input name="email" type="email" placeholder="ornek@mail.com" style="width:100%;padding:9px 12px;border:1px solid #d1d5db;border-radius:8px;font-size:14px;box-sizing:border-box"></div>'\
    + '<div style="margin-bottom:20px"><label style="display:block;font-size:13px;color:#374151;margin-bottom:6px">Mesaj\\u0131n\\u0131z</label>'\
    +   '<textarea name="message" rows="3" placeholder="Mesaj\\u0131n\\u0131z\\u0131 yaz\\u0131n..." style="width:100%;padding:9px 12px;border:1px solid #d1d5db;border-radius:8px;font-size:14px;resize:vertical;box-sizing:border-box"></textarea></div>'\
    + '<input name="hp_website" style="position:absolute;left:-9999px;opacity:0;height:0" tabindex="-1" autocomplete="off">'\
    + '<input type="hidden" name="_ft" id="yapicrm-ft">'\
    + '<button type="submit" id="yapicrm-btn" style="width:100%;padding:11px;background:#cd6906;color:#fff;border:none;border-radius:8px;font-size:15px;font-weight:600;cursor:pointer">${esc(formButon)}</button>'\
    + '<p id="yapicrm-err" style="margin:10px 0 0;font-size:13px;text-align:center;color:#991b1b;display:none"></p>'\
    + '</form>';
  document.getElementById('yapicrm-ft').value = Date.now();
  document.getElementById('yapicrm-form').addEventListener('submit', function(e){
    e.preventDefault();
    var btn = document.getElementById('yapicrm-btn');
    var err = document.getElementById('yapicrm-err');
    var f = e.target;
    err.style.display = 'none';
    btn.disabled = true; btn.textContent = 'G\\u00f6nderiliyor...';
    fetch('${endpoint}', {
      method: 'POST',
      headers: { 'Authorization': 'Basic ${token}', 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fullName: f.fullName.value,
        phone: f.phone.value,
        email: f.email.value,
        message: f.message.value,
        hpWebsite: f.hp_website.value,
        ft: parseInt(f._ft.value) || 0
      })
    })
    .then(function(r){ return r.json(); })
    .then(function(){
      wrap.innerHTML =
        '<div style="max-width:480px;font-family:Arial,sans-serif;padding:40px 24px;border:1px solid #bbf7d0;border-radius:12px;background:#f0fdf4;text-align:center">'\
        + '<div style="font-size:52px;color:#16a34a;margin-bottom:16px">\\u2713</div>'\
        + '<p style="font-size:16px;font-weight:600;color:#14532d;margin:0 0 8px">${esc(formBasari)}</p>'\
        + '</div>';
    })
    .catch(function(){
      btn.disabled = false; btn.textContent = '${esc(formButon)}';
      err.style.display = 'block';
      err.textContent = 'Bir hata olu\\u015ftu, l\\u00fctfen tekrar deneyiniz.';
    });
  });
})();
<\/script>`;
        setGeneratedCode(code);
    };

    // ── Sheets fonksiyonları ──────────────────────────────────

    const fetchSheets = async () => {
        setSheetsLoading(true);
        try {
            const res = await axiosInstance.get('/api/SheetsEntegrasyon');
            setSheetsListesi(res.data);
        } catch { toast.error(t('msg.islemHatasi')); }
        finally { setSheetsLoading(false); }
    };

    const sheetsFormTemizle = () => {
        setSheetsEditId(null); setSheetsAd(''); setSheetsUrl(''); setSheetsAralik(60);
        setSheetsBasliklar([]);
        setSheetsSutunPk(''); setSheetsSutunAdiSoyadi(''); setSheetsSutunTelefon('');
        setSheetsSutunEposta(''); setSheetsSutunAciklama('');
        setSheetsVarsayilanKaynak('Google Sheets'); setSheetsVarsayilanDurum('');
        setSheetsForm(false);
    };

    const handleSheetsOnizleme = async () => {
        if (!sheetsUrl.trim()) { toast.warning('Sheets URL giriniz.'); return; }
        setSheetsPreviewLoading(true);
        try {
            const res = await axiosInstance.post('/api/SheetsEntegrasyon/onizleme', { sheetsUrl: sheetsUrl.trim() });
            setSheetsBasliklar(res.data.basliklar);
            toast.success(`${res.data.basliklar.length} sütun, ${res.data.toplamSatir} kayıt bulundu.`);
        } catch (err: any) {
            toast.error(err?.response?.data?.message || 'Sheets verisi alınamadı. Belgeyi herkese açık yapın.');
        } finally { setSheetsPreviewLoading(false); }
    };

    const handleSheetsSave = async () => {
        if (!sheetsAd.trim() || !sheetsUrl.trim()) { toast.warning('Ad ve URL zorunludur.'); return; }
        if (sheetsBasliklar.length > 0 && sheetsSutunAdiSoyadi === '' && sheetsSutunTelefon === '') {
            toast.warning('En az Ad Soyad veya Telefon sütunu seçilmelidir.'); return;
        }
        setSheetsSaving(true);
        const payload = {
            ad: sheetsAd, sheetsUrl, aralikDakika: sheetsAralik,
            sutunPk: sheetsSutunPk === '' ? null : sheetsSutunPk,
            sutunAdiSoyadi: sheetsSutunAdiSoyadi === '' ? null : sheetsSutunAdiSoyadi,
            sutunTelefon: sheetsSutunTelefon === '' ? null : sheetsSutunTelefon,
            sutunEposta: sheetsSutunEposta === '' ? null : sheetsSutunEposta,
            sutunAciklama: sheetsSutunAciklama === '' ? null : sheetsSutunAciklama,
            varsayilanKaynak: sheetsVarsayilanKaynak || null,
            varsayilanDurumId: sheetsVarsayilanDurum === '' ? null : sheetsVarsayilanDurum,
        };
        try {
            if (sheetsEditId) {
                await axiosInstance.post(`/api/SheetsEntegrasyon/update/${sheetsEditId}`, payload);
            } else {
                await axiosInstance.post('/api/SheetsEntegrasyon', payload);
            }
            toast.success(t('msg.islemBasarili'));
            sheetsFormTemizle();
            fetchSheets();
        } catch (err: any) {
            toast.error(err?.response?.data?.message || t('msg.islemHatasi'));
        } finally { setSheetsSaving(false); }
    };

    const handleSheetsEdit = (ent: any) => {
        setSheetsEditId(ent.id); setSheetsAd(ent.ad); setSheetsUrl(ent.sheetsUrl);
        setSheetsAralik(ent.aralikDakika); setSheetsBasliklar([]);
        setSheetsSutunPk(ent.sutunPk ?? '');
        setSheetsSutunAdiSoyadi(ent.sutunAdiSoyadi ?? '');
        setSheetsSutunTelefon(ent.sutunTelefon ?? '');
        setSheetsSutunEposta(ent.sutunEposta ?? '');
        setSheetsSutunAciklama(ent.sutunAciklama ?? '');
        setSheetsVarsayilanKaynak(ent.varsayilanKaynak ?? 'Google Sheets');
        setSheetsVarsayilanDurum(ent.varsayilanDurumId ?? '');
        setSheetsForm(true);
    };

    const handleSheetsDelete = async (id: number) => {
        try {
            await axiosInstance.post(`/api/SheetsEntegrasyon/delete/${id}`);
            setSheetsListesi(prev => prev.filter(e => e.id !== id));
            toast.success(t('msg.islemBasarili'));
        } catch { toast.error(t('msg.islemHatasi')); }
    };

    const handleSheetsToggle = async (id: number) => {
        try {
            const res = await axiosInstance.post(`/api/SheetsEntegrasyon/toggle/${id}`);
            setSheetsListesi(prev => prev.map(e => e.id === id ? { ...e, aktifMi: res.data.aktifMi } : e));
        } catch { toast.error(t('msg.islemHatasi')); }
    };

    const handleSheetsSync = async (id: number) => {
        setSheetsSyncing(id);
        try {
            const res = await axiosInstance.post(`/api/SheetsEntegrasyon/sync/${id}`);
            toast.success(res.data.message);
            fetchSheets();
        } catch (err: any) {
            toast.error(err?.response?.data?.message || t('msg.islemHatasi'));
        } finally { setSheetsSyncing(null); }
    };

    const handleSheetsResetSync = async (id: number) => {
        try {
            await axiosInstance.post(`/api/SheetsEntegrasyon/reset-sync/${id}`);
            toast.success('Senkronizasyon sıfırlandı. Tüm satırlar bir sonraki senkronda işlenecek.');
            fetchSheets();
        } catch { toast.error(t('msg.islemHatasi')); }
    };

    const sutunSecenegi = (idx: number, baslik: string) => ({ label: `${idx}: ${baslik}`, value: idx });
    const aralikSecenekleri = [{ v: 15, l: '15 dakika' }, { v: 30, l: '30 dakika' }, { v: 60, l: '1 saat' }, { v: 360, l: '6 saat' }, { v: 1440, l: 'Günlük' }];

    const tabStyle = (tab: string): React.CSSProperties => ({
        padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer',
        fontSize: 13, fontWeight: 500,
        background: activeTab === tab ? '#cd6906' : 'transparent',
        color: activeTab === tab ? '#fff' : '#6b7280',
        transition: 'all 0.15s',
    });

    if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>{t('msg.yukleniyor')}</div>;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Başlık */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Link size={18} color="#cd6906" />
                <div>
                    <div style={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>Dış Kaynak API Entegrasyonu</div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                        Web sitesi, form veya üçüncü taraf uygulamalardan potansiyel müşteri verisi alın
                    </div>
                </div>
            </div>

            {/* Sekmeler */}
            <div style={{ display: 'flex', gap: 4, background: '#f3f4f6', borderRadius: 10, padding: 4, width: 'fit-content' }}>
                <button style={tabStyle('kullanicilar')} onClick={() => setActiveTab('kullanicilar')}>
                    API Kullanıcıları
                </button>
                <button style={tabStyle('ayarlar')} onClick={() => setActiveTab('ayarlar')}>
                    Varsayılan Ayarlar
                </button>
                <button style={tabStyle('kilavuz')} onClick={() => setActiveTab('kilavuz')}>
                    Entegrasyon Kılavuzu
                </button>
                <button style={{ ...tabStyle('formkodu'), display: 'flex', alignItems: 'center', gap: 5 }} onClick={() => setActiveTab('formkodu')}>
                    <Code size={13} /> Form Kodu Oluştur
                </button>
                <button style={{ ...tabStyle('sheets'), display: 'flex', alignItems: 'center', gap: 5 }} onClick={() => { setActiveTab('sheets'); fetchSheets(); }}>
                    <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M3 15h18M9 3v18"/></svg>
                    Online Sheets
                </button>
            </div>

            {/* ── API Kullanıcıları ── */}
            {activeTab === 'kullanicilar' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* Yeni kullanıcı oluştur */}
                    <div style={{ ...card }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Plus size={14} color="#cd6906" /> Yeni API Kullanıcısı Oluştur
                        </div>
                        <div style={{ display: 'flex', gap: 10 }}>
                            <input
                                type="text"
                                value={newAd}
                                onChange={e => setNewAd(e.target.value)}
                                placeholder={t('lead.kullaniciEtiketi')}
                                style={{ ...inp, flex: 1 }}
                                onKeyDown={e => { if (e.key === 'Enter') handleCreateKullanici(); }}
                            />
                            <button
                                onClick={handleCreateKullanici}
                                disabled={creating}
                                style={{
                                    padding: '8px 18px', borderRadius: 8, border: 'none',
                                    background: creating ? '#e5e7eb' : '#cd6906',
                                    color: creating ? '#9ca3af' : '#fff',
                                    fontSize: 13, fontWeight: 500, cursor: creating ? 'not-allowed' : 'pointer',
                                    display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
                                }}
                            >
                                <Plus size={14} /> Oluştur
                            </button>
                        </div>
                    </div>

                    {/* Oluşturulan şifre gösterimi */}
                    {createdSecret && (
                        <div style={{ ...card, border: '1px solid #fbbf24', background: '#fffbeb' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: '#92400e', marginBottom: 12 }}>
                                <Info size={14} /> API Şifresi — Sadece Bu Seferlik Gösterilmektedir
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ ...lbl }}>API Key</div>
                                        <div style={{ display: 'flex', gap: 6 }}>
                                            <code style={{ flex: 1, padding: '7px 10px', background: '#fff', border: '1px solid #fcd34d', borderRadius: 6, fontSize: 13, fontFamily: 'monospace' }}>
                                                {createdSecret.apiKey}
                                            </code>
                                            <button onClick={() => copyToClipboard(createdSecret.apiKey, 'API Key')} style={{ background: 'none', border: '1px solid #fcd34d', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', color: '#92400e' }}>
                                                <Copy size={13} />
                                            </button>
                                        </div>
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ ...lbl }}>API Secret</div>
                                        <div style={{ display: 'flex', gap: 6 }}>
                                            <code style={{ flex: 1, padding: '7px 10px', background: '#fff', border: '1px solid #fcd34d', borderRadius: 6, fontSize: 13, fontFamily: 'monospace', letterSpacing: showSecret ? 0 : 3 }}>
                                                {showSecret ? createdSecret.apiSecret : '••••••••••••••••••••••'}
                                            </code>
                                            <button onClick={() => setShowSecret(s => !s)} style={{ background: 'none', border: '1px solid #fcd34d', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', color: '#92400e' }}>
                                                {showSecret ? <EyeOff size={13} /> : <Eye size={13} />}
                                            </button>
                                            <button onClick={() => copyToClipboard(createdSecret.apiSecret, 'API Secret')} style={{ background: 'none', border: '1px solid #fcd34d', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', color: '#92400e' }}>
                                                <Copy size={13} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <p style={{ fontSize: 12, color: '#92400e', margin: 0 }}>
                                    Bu şifreyi şimdi kopyalayın. Sayfayı kapattıktan sonra bir daha gösterilmeyecektir.
                                </p>
                                <button onClick={() => setCreatedSecret(null)} style={{ alignSelf: 'flex-end', background: 'none', border: '1px solid #fcd34d', borderRadius: 6, padding: '5px 14px', fontSize: 12, color: '#92400e', cursor: 'pointer' }}>
                                    Tamam, Anladım
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Kullanıcı listesi */}
                    <div style={{ ...card }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Key size={14} color="#cd6906" /> API Kullanıcıları ({kullanicilar.length})
                        </div>
                        {kullanicilar.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '32px 0', color: '#9ca3af', fontSize: 13 }}>
                                Henüz API kullanıcısı oluşturulmadı.
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {kullanicilar.map(k => (
                                    <div key={k.id} style={{
                                        display: 'flex', alignItems: 'center', gap: 12,
                                        padding: '10px 14px', borderRadius: 8, background: k.aktifMi ? '#f9fafb' : '#fef2f2',
                                        border: `1px solid ${k.aktifMi ? '#e5e7eb' : '#fecaca'}`,
                                    }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>{k.ad}</div>
                                            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2, fontFamily: 'monospace' }}>
                                                API Key: {k.apiKey}
                                            </div>
                                        </div>
                                        <div style={{ fontSize: 11, color: '#9ca3af' }}>
                                            {new Date(k.olusturmaTarihi).toLocaleDateString('tr-TR')}
                                        </div>
                                        <span style={{
                                            fontSize: 11, fontWeight: 500, padding: '2px 10px', borderRadius: 20,
                                            background: k.aktifMi ? '#d1fae5' : '#fee2e2',
                                            color: k.aktifMi ? '#065f46' : '#991b1b',
                                        }}>
                                            {k.aktifMi ? 'Aktif' : 'Pasif'}
                                        </span>
                                        <button
                                            onClick={() => handleToggle(k.id)}
                                            title={k.aktifMi ? 'Pasife Al' : 'Aktife Al'}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: k.aktifMi ? '#059669' : '#9ca3af', display: 'flex' }}
                                        >
                                            {k.aktifMi ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                                        </button>
                                        <button
                                            onClick={() => handleDelete(k.id)}
                                            title={t('btn.sil')}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', display: 'flex' }}
                                        >
                                            <Trash2 size={15} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ── Varsayılan Ayarlar ── */}
            {activeTab === 'ayarlar' && (
                <div style={{ ...card, maxWidth: 480 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 16 }}>
                        API üzerinden gelen kayıtlar için varsayılan değerler
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <div>
                            <label style={lbl}>Varsayılan Kaynak</label>
                            <select
                                value={ayarlar.varsayilanKaynak ?? ''}
                                onChange={e => setAyarlar({ ...ayarlar, varsayilanKaynak: e.target.value })}
                                style={{ ...inp }}
                            >
                                {KAYNAK_LISTESI.map(k => (
                                    <option key={k} value={k}>{k}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label style={lbl}>Varsayılan Durum</label>
                            <select
                                value={ayarlar.varsayilanDurum ?? ''}
                                onChange={e => setAyarlar({ ...ayarlar, varsayilanDurum: e.target.value ? Number(e.target.value) : null })}
                                style={{ ...inp }}
                            >
                                <option value="">— Seçiniz —</option>
                                {durumlar.map(d => (
                                    <option key={d.statusID} value={d.statusID}>{d.statusAdi}</option>
                                ))}
                            </select>
                        </div>
                        <button
                            onClick={handleSaveAyarlar}
                            disabled={savingAyarlar}
                            style={{
                                padding: '9px 20px', borderRadius: 8, border: 'none',
                                background: savingAyarlar ? '#e5e7eb' : '#cd6906',
                                color: savingAyarlar ? '#9ca3af' : '#fff',
                                fontSize: 13, fontWeight: 600, cursor: savingAyarlar ? 'not-allowed' : 'pointer',
                                display: 'flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start',
                            }}
                        >
                            <Save size={14} /> Kaydet
                        </button>
                    </div>
                </div>
            )}

            {/* ── Entegrasyon Kılavuzu ── */}
            {activeTab === 'kilavuz' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* Genel Bilgi */}
                    <div style={{ ...card, background: '#f0f9ff', border: '1px solid #bae6fd' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#0c4a6e', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Info size={14} /> API Hakkında
                        </div>
                        <p style={{ fontSize: 13, color: '#075985', margin: 0, lineHeight: 1.7 }}>
                            Bu API, web siteniz, iletişim formunuz veya herhangi bir üçüncü taraf uygulama aracılığıyla
                            potansiyel müşteri bilgilerini CRM sistemine göndermenizi sağlar. Her bağlantı için ayrı bir
                            API kullanıcısı oluşturmanızı öneririz. İstekler <strong>Basic Authentication</strong> ile doğrulanır.
                        </p>
                    </div>

                    {/* Endpoint */}
                    <div style={{ ...card }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 12 }}>Endpoint</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#f3f4f6', borderRadius: 8 }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#fff', background: '#16a34a', padding: '2px 8px', borderRadius: 4 }}>POST</span>
                            <code style={{ fontSize: 13, fontFamily: 'monospace', color: '#1e293b', flex: 1 }}>
                                {apiBaseUrl}/api/LeadApi/submit
                            </code>
                            <button onClick={() => copyToClipboard(`${apiBaseUrl}/api/LeadApi/submit`, 'URL')} style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', color: '#6b7280' }}>
                                <Copy size={12} />
                            </button>
                        </div>
                    </div>

                    {/* Header */}
                    <div style={{ ...card }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 12 }}>Kimlik Doğrulama (Basic Auth)</div>
                        <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 10px' }}>
                            Her istekte <code style={{ background: '#f3f4f6', padding: '1px 6px', borderRadius: 4 }}>Authorization</code> header'ı gönderilmelidir.
                            Değer <code style={{ background: '#f3f4f6', padding: '1px 6px', borderRadius: 4 }}>Basic &lt;base64(API_KEY:API_SECRET)&gt;</code> formatındadır.
                        </p>
                        <pre style={{ background: '#1e293b', color: '#e2e8f0', padding: '14px 16px', borderRadius: 8, fontSize: 12, overflowX: 'auto', margin: 0 }}>
{`Authorization: Basic <base64 kodlanmış API_KEY:API_SECRET>
Content-Type: application/json`}
                        </pre>
                    </div>

                    {/* İstek gövdesi */}
                    <div style={{ ...card }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 12 }}>İstek Gövdesi (JSON)</div>
                        <pre style={{ background: '#1e293b', color: '#e2e8f0', padding: '14px 16px', borderRadius: 8, fontSize: 12, overflowX: 'auto', margin: 0 }}>
{`{
  "fullName": "Ahmet Yılmaz",    // Zorunlu
  "phone":    "05551234567",     // Zorunlu
  "email":    "ahmet@ornek.com", // İsteğe bağlı
  "message":  "Bilgi almak istiyorum" // İsteğe bağlı
}`}
                        </pre>
                    </div>

                    {/* Örnek: cURL */}
                    <div style={{ ...card }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Örnek: cURL</div>
                            <button
                                onClick={() => copyToClipboard(
                                    `curl -X POST ${apiBaseUrl}/api/LeadApi/submit \\\n  -H "Authorization: Basic $(echo -n 'API_KEY:API_SECRET' | base64)" \\\n  -H "Content-Type: application/json" \\\n  -d '{"fullName":"Ahmet Yılmaz","phone":"05551234567","email":"ahmet@ornek.com","message":"Bilgi almak istiyorum"}'`,
                                    'cURL kodu'
                                )}
                                style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 }}
                            >
                                <Copy size={12} /> Kopyala
                            </button>
                        </div>
                        <pre style={{ background: '#1e293b', color: '#e2e8f0', padding: '14px 16px', borderRadius: 8, fontSize: 12, overflowX: 'auto', margin: 0 }}>
{`curl -X POST ${apiBaseUrl}/api/LeadApi/submit \\
  -H "Authorization: Basic $(echo -n 'API_KEY:API_SECRET' | base64)" \\
  -H "Content-Type: application/json" \\
  -d '{
    "fullName": "Ahmet Yılmaz",
    "phone":    "05551234567",
    "email":    "ahmet@ornek.com",
    "message":  "Bilgi almak istiyorum"
  }'`}
                        </pre>
                    </div>

                    {/* Örnek: JavaScript/Fetch */}
                    <div style={{ ...card }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Örnek: JavaScript / Fetch</div>
                        </div>
                        <pre style={{ background: '#1e293b', color: '#e2e8f0', padding: '14px 16px', borderRadius: 8, fontSize: 12, overflowX: 'auto', margin: 0 }}>
{`const apiKey    = 'API_KEY';
const apiSecret = 'API_SECRET';
const token     = btoa(\`\${apiKey}:\${apiSecret}\`);

fetch('${apiBaseUrl}/api/LeadApi/submit', {
  method: 'POST',
  headers: {
    'Authorization': \`Basic \${token}\`,
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    fullName: 'Ahmet Yılmaz',
    phone:    '05551234567',
    email:    'ahmet@ornek.com',
    message:  'Bilgi almak istiyorum',
  }),
});`}
                        </pre>
                    </div>

                    {/* Yanıt */}
                    <div style={{ ...card }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 12 }}>Başarılı Yanıt (200 OK)</div>
                        <pre style={{ background: '#1e293b', color: '#e2e8f0', padding: '14px 16px', borderRadius: 8, fontSize: 12, overflowX: 'auto', margin: 0 }}>
{`{
  "message": "Kayıt başarıyla alındı.",
  "leadId":  42
}`}
                        </pre>
                    </div>
                </div>
            )}

            {/* ── Form Kodu Oluştur ── */}
            {activeTab === 'formkodu' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div style={{ ...card, background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#14532d', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Info size={14} /> Nasıl Çalışır?
                        </div>
                        <p style={{ fontSize: 13, color: '#166534', margin: 0, lineHeight: 1.7 }}>
                            API Key ve Secret bilgilerinizi girin, formu özelleştirin ve oluşturulan kodu
                            web sitenizin istediğiniz yerine yapıştırın. Form gönderildiğinde müşteri bilgileri
                            otomatik olarak CRM'e aktarılır.
                        </p>
                    </div>

                    <div style={{ ...card }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 16 }}>1. API Kimlik Bilgileri</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                            <div>
                                <label style={lbl}>API Key *</label>
                                <input type="text" value={formApiKey} onChange={e => setFormApiKey(e.target.value)}
                                    placeholder={t('label.ara')} style={inp} />
                            </div>
                            <div>
                                <label style={lbl}>API Secret *</label>
                                <div style={{ display: 'flex', gap: 6 }}>
                                    <input type={showFormSecret ? 'text' : 'password'} value={formApiSecret}
                                        onChange={e => setFormApiSecret(e.target.value)}
                                        placeholder={t('label.ara')}
                                        style={{ ...inp, flex: 1 }} />
                                    <button onClick={() => setShowFormSecret(s => !s)}
                                        style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 8, padding: '0 10px', cursor: 'pointer', color: '#6b7280', display: 'flex', alignItems: 'center' }}>
                                        {showFormSecret ? <EyeOff size={14} /> : <Eye size={14} />}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div style={{ ...card }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 16 }}>2. Form Özelleştirme</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                            <div>
                                <label style={lbl}>Form Başlığı</label>
                                <input type="text" value={formBaslik} onChange={e => setFormBaslik(e.target.value)} style={inp} />
                            </div>
                            <div>
                                <label style={lbl}>Buton Metni</label>
                                <input type="text" value={formButon} onChange={e => setFormButon(e.target.value)} style={inp} />
                            </div>
                            <div style={{ gridColumn: '1 / -1' }}>
                                <label style={lbl}>Başarı Mesajı</label>
                                <input type="text" value={formBasari} onChange={e => setFormBasari(e.target.value)} style={inp} />
                            </div>
                        </div>
                    </div>

                    <button onClick={generateFormCode} style={{
                        padding: '11px 28px', borderRadius: 8, border: 'none', background: '#cd6906', color: '#fff',
                        fontSize: 14, fontWeight: 600, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 8, alignSelf: 'flex-start',
                        boxShadow: '0 2px 8px rgba(205,105,6,0.3)',
                    }}>
                        <Code size={15} /> Form Kodunu Oluştur
                    </button>

                    {generatedCode && (
                        <div style={{ ...card }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>3. Kodu Kopyalayın ve Web Sitenize Yapıştırın</div>
                                <button onClick={() => copyToClipboard(generatedCode, 'Form kodu')}
                                    style={{ padding: '7px 16px', borderRadius: 8, border: 'none', background: '#cd6906', color: '#fff', fontSize: 12, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                                    <Copy size={13} /> Kodu Kopyala
                                </button>
                            </div>
                            <textarea readOnly value={generatedCode} rows={22}
                                style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 8, background: '#f8fafc', fontFamily: 'monospace', fontSize: 12, padding: '12px 16px', resize: 'vertical', outline: 'none', color: '#1e293b', boxSizing: 'border-box', lineHeight: 1.6 }} />
                            <div style={{ marginTop: 10, padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 12, color: '#92400e' }}>
                                <strong>Not:</strong> Her site için ayrı API kullanıcısı oluşturun. Bir bağlantı tehlikeye girerse yalnızca o kullanıcıyı pasife alabilirsiniz.
                            </div>
                        </div>
                    )}
                </div>
            )}
            {/* ── Online Sheets ── */}
            {activeTab === 'sheets' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

                    {/* Bilgi notu */}
                    <div style={{ ...card, background: '#eff6ff', border: '1px solid #bfdbfe' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#1e40af', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Info size={14} /> Nasıl Çalışır?
                        </div>
                        <p style={{ fontSize: 13, color: '#1d4ed8', margin: 0, lineHeight: 1.7 }}>
                            Google Sheets belgenizi <strong>"Linkle görebilir"</strong> (public) yapın.
                            Sheets URL'ini girin, sütunları eşleştirin. Sistem belirlediğiniz aralıkla sayfayı kontrol ederek yeni satırları otomatik olarak Potansiyel Müşteri listesine ekler.
                        </p>
                    </div>

                    {/* Yeni entegrasyon butonu */}
                    {!sheetsForm && (
                        <button onClick={() => { sheetsFormTemizle(); setSheetsForm(true); }}
                            style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 8, border: 'none', background: '#cd6906', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                            <Plus size={14} /> Yeni Entegrasyon Ekle
                        </button>
                    )}

                    {/* Form */}
                    {sheetsForm && (
                        <div style={{ ...card }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 18 }}>
                                {sheetsEditId ? 'Entegrasyonu Düzenle' : 'Yeni Google Sheets Entegrasyonu'}
                            </div>

                            {/* Temel bilgiler */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                                <div>
                                    <label style={lbl}>Entegrasyon Adı *</label>
                                    <input type="text" value={sheetsAd} onChange={e => setSheetsAd(e.target.value)} placeholder="örn: Web Formu Kayıtları" style={inp} />
                                </div>
                                <div>
                                    <label style={lbl}>Senkronizasyon Aralığı</label>
                                    <select value={sheetsAralik} onChange={e => setSheetsAralik(Number(e.target.value))} style={inp}>
                                        {aralikSecenekleri.map(a => <option key={a.v} value={a.v}>{a.l}</option>)}
                                    </select>
                                </div>
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <label style={lbl}>Google Sheets URL *</label>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <input type="text" value={sheetsUrl} onChange={e => { setSheetsUrl(e.target.value); setSheetsBasliklar([]); }}
                                            placeholder="https://docs.google.com/spreadsheets/d/..." style={{ ...inp, flex: 1 }} />
                                        <button onClick={handleSheetsOnizleme} disabled={sheetsPreviewLoading}
                                            style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #cd6906', background: '#fff', color: '#cd6906', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', opacity: sheetsPreviewLoading ? 0.7 : 1 }}>
                                            {sheetsPreviewLoading ? '...' : 'Başlıkları Getir'}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Sütun eşleştirme — başlıklar yüklendikten sonra aktif olur */}
                            {sheetsBasliklar.length > 0 && (
                                <>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', margin: '18px 0 12px', paddingTop: 14, borderTop: '1px solid #f3f4f6' }}>
                                        Sütun Eşleştirme
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                                        {([
                                            { label: 'Tekil Anahtar (PK)', val: sheetsSutunPk, set: setSheetsSutunPk, hint: 'Duplicate tespiti için benzersiz alan (ör: id)' },
                                            { label: 'Ad Soyad *', val: sheetsSutunAdiSoyadi, set: setSheetsSutunAdiSoyadi, hint: '' },
                                            { label: 'Telefon *', val: sheetsSutunTelefon, set: setSheetsSutunTelefon, hint: '' },
                                            { label: 'E-posta', val: sheetsSutunEposta, set: setSheetsSutunEposta, hint: '' },
                                            { label: 'Açıklama (Görüşme Notu)', val: sheetsSutunAciklama, set: setSheetsSutunAciklama, hint: '' },
                                        ] as { label: string; val: number | ''; set: (v: number | '') => void; hint: string }[]).map(({ label, val, set, hint }) => (
                                            <div key={label}>
                                                <label style={lbl}>{label}</label>
                                                {hint && <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 4 }}>{hint}</div>}
                                                <select value={val} onChange={e => set(e.target.value === '' ? '' : Number(e.target.value))} style={inp}>
                                                    <option value="">— Seçilmedi —</option>
                                                    {sheetsBasliklar.map((b, i) => (
                                                        <option key={i} value={i}>{b}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}

                            {/* Varsayılan değerler — her zaman görünür */}
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', margin: '18px 0 12px', paddingTop: 14, borderTop: '1px solid #f3f4f6' }}>
                                Varsayılan Değerler
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                                <div>
                                    <label style={lbl}>Kaynak</label>
                                    <select value={sheetsVarsayilanKaynak} onChange={e => setSheetsVarsayilanKaynak(e.target.value)} style={inp}>
                                        {KAYNAK_SHEETS.map(k => <option key={k} value={k}>{k}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label style={lbl}>Durum</label>
                                    <select value={sheetsVarsayilanDurum} onChange={e => setSheetsVarsayilanDurum(e.target.value === '' ? '' : Number(e.target.value))} style={inp}>
                                        <option value="">— Seçilmedi —</option>
                                        {durumlar.map(d => <option key={d.statusID} value={d.statusID}>{d.statusAdi}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                <button onClick={handleSheetsSave} disabled={sheetsSaving || (!sheetsEditId && sheetsBasliklar.length === 0)}
                                    style={{ padding: '9px 22px', borderRadius: 8, border: 'none', background: '#cd6906', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: (sheetsSaving || (!sheetsEditId && sheetsBasliklar.length === 0)) ? 0.6 : 1 }}>
                                    {sheetsSaving ? 'Kaydediliyor...' : (sheetsEditId ? 'Güncelle' : 'Kaydet')}
                                </button>
                                <button onClick={sheetsFormTemizle} style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#6b7280', fontSize: 13, cursor: 'pointer' }}>
                                    İptal
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Liste */}
                    {sheetsLoading ? (
                        <div style={{ textAlign: 'center', padding: 30, color: '#9ca3af', fontSize: 13 }}>Yükleniyor...</div>
                    ) : sheetsListesi.length === 0 && !sheetsForm ? (
                        <div style={{ ...card, textAlign: 'center', padding: 40, color: '#9ca3af', fontSize: 13 }}>
                            Henüz Google Sheets entegrasyonu eklenmedi.
                        </div>
                    ) : sheetsListesi.map(ent => (
                        <div key={ent.id} style={{ ...card, opacity: ent.aktifMi ? 1 : 0.6 }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                        <span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{ent.ad}</span>
                                        <span style={{ padding: '2px 8px', borderRadius: 9999, fontSize: 11, fontWeight: 600, background: ent.aktifMi ? '#dcfce7' : '#f3f4f6', color: ent.aktifMi ? '#16a34a' : '#6b7280' }}>
                                            {ent.aktifMi ? '● Aktif' : '○ Pasif'}
                                        </span>
                                        <span style={{ padding: '2px 8px', borderRadius: 9999, fontSize: 11, fontWeight: 500, background: '#f3f4f6', color: '#6b7280' }}>
                                            {aralikSecenekleri.find(a => a.v === ent.aralikDakika)?.l ?? `${ent.aralikDakika} dk`}
                                        </span>
                                    </div>
                                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 500 }}>{ent.sheetsUrl}</div>
                                    <div style={{ fontSize: 12, color: '#9ca3af', display: 'flex', gap: 16 }}>
                                        <span>Toplam aktarılan: <strong style={{ color: '#374151' }}>{ent.toplamAktarilan}</strong></span>
                                        <span>Son senkron: <strong style={{ color: '#374151' }}>{ent.sonSenkronTarihi ? new Date(ent.sonSenkronTarihi).toLocaleString('tr-TR') : '—'}</strong></span>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                                    <button onClick={() => handleSheetsSync(ent.id)} disabled={sheetsSyncing === ent.id}
                                        style={{ padding: '6px 12px', borderRadius: 7, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                                        {sheetsSyncing === ent.id ? '...' : '↻ Şimdi Senkronize Et'}
                                    </button>
                                    <button onClick={() => handleSheetsResetSync(ent.id)}
                                        title="Tüm satırları yeniden aktarmak için sayacı sıfırla"
                                        style={{ padding: '6px 12px', borderRadius: 7, border: '1px solid #fbbf24', background: '#fffbeb', color: '#92400e', fontSize: 12, cursor: 'pointer' }}>
                                        ↺ Sıfırla
                                    </button>
                                    <button onClick={() => handleSheetsEdit(ent)}
                                        style={{ padding: '6px 12px', borderRadius: 7, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: 12, cursor: 'pointer' }}>
                                        Düzenle
                                    </button>
                                    <button onClick={() => handleSheetsToggle(ent.id)}
                                        style={{ padding: '6px 12px', borderRadius: 7, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: 12, cursor: 'pointer' }}>
                                        {ent.aktifMi ? 'Pasife Al' : 'Aktif Et'}
                                    </button>
                                    <button onClick={() => handleSheetsDelete(ent.id)}
                                        style={{ padding: '6px 12px', borderRadius: 7, border: '1px solid #fecaca', background: '#fff', color: '#b91c1c', fontSize: 12, cursor: 'pointer' }}>
                                        Sil
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

        </div>
    );
};

export default LeadApiAyarlariPage;
